/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed, set } from '@ember/object';

import BaseOptimization from './base-optimization';
import NoiseFormula from '../formulas/noise';
import FilterConstants from '../../filter-constants';
import * as NoiseUtils from '../utils/noise';
import * as PermutationUtils from '../utils/permutation';
import CircuitGroupResults from '../results/circuit-group-results';

export default class NoiseOptimization extends BaseOptimization {
  name = FilterConstants.optimizationType.noise;
  noise = new NoiseFormula();
  @computed('stopbandFrequency') get opAmpRequirementSpecValues() {
    var base = this;
    var stopbandFrequency = base.stopbandFrequency;
    var result = this.getOpAmpRequirementSpecValues(stopbandFrequency);
    return result;
  }

  determineCapacitorValue(stageFp) {
    var startingCapacitor = null;
    switch (true) {
      //fp is below 159 Hz
      case stageFp < 159:
        //1/(2*pi*fp*10kohm) in nF
        startingCapacitor = 1 / (2 * Math.PI * stageFp * 10000);
        break;

      //fp is 159 Hz to 1.59 kHz
      case stageFp >= 159 && stageFp <= 1590:
        //100 nF
        startingCapacitor = 100 * 1e-9;
        break;

      //fp is 1.59 kHz to 159kHz
      case stageFp >= 1590 && stageFp <= 159000:
        //1/(2*PI*fp*1kohm) in nF
        startingCapacitor = 1 / (2 * Math.PI * stageFp * 1000);
        break;

      //fp is 159 kHz to 1.59MHz
      case stageFp >= 159000 && stageFp <= 1590000:
        //1 nF
        startingCapacitor = 1 * 1e-9;
        break;

      //fp is above 1.59MHz
      case stageFp > 1590000:
        //1/(2*PI*fp*100ohms) in nF
        startingCapacitor = 1 / (2 * Math.PI * stageFp * 100);
        break;
    }

    return startingCapacitor;
  }

  determineStartingR3R4Value(stageFp) {
    var base = this; // to specify which one comes from the base class;
    var rMin = 100;
    var rMax = 10000;
    var result = base.processR3R4Value(stageFp, rMin, rMax);
    return result;
  }

  getOpAmpRequirementSpecValues(stopbandFrequency) {
    var base = this; // to specify which one comes from the base class;
    var specs = {
      fMultiplier: 200, //bigger is more strict:  used for highpass
      allowedError: 0.02, //smaller is more strict: used lowpass and bandpass
      rOutFreqMultiplier: 5, //rout shouldn't come into play until 5x stopband
      dcOutputVoltageFromIBias: 0.2,
      stopbandFrequency: stopbandFrequency,
      inductorMultiplier: 4,
    };

    specs.f = stopbandFrequency * specs.rOutFreqMultiplier;

    var result = base.adjustOpAmpRequirementSpecs(specs, stopbandFrequency);

    return result;
  }

  optimizeStageOrder(stages, filterFrequencyRange) {
    var newFrequencyArray = this.computeOptimizationFrequencyArray(filterFrequencyRange);
    var stageLength = stages.length;
    var permutations = PermutationUtils.permutation(stageLength, stageLength);
    var adcBandwidth = Math.max.apply(Math, newFrequencyArray);

    let circuitGroupResults = CircuitGroupResults.create({
      actualStages: stages,
      adcBandwidth: adcBandwidth,
      frequencyArray: newFrequencyArray,
    });
    let lowestNoise = null;
    let lowestNoiseSortOrder = null;
    let rmsNoiseArray = [];
    permutations.forEach((permutation, index) => {
      set(circuitGroupResults, 'sortOrder', permutation);
      let lastCircuit = circuitGroupResults.lastCircuitNoiseGroup;
      let noiseArray = lastCircuit.totalSpectralNoiseReferredToOutputArray;
      let rmsNoise = NoiseUtils.getRmsNoise(noiseArray, adcBandwidth, newFrequencyArray);
      rmsNoiseArray.push(rmsNoise);
      if (index === 0) {
        lowestNoise = rmsNoise;
        lowestNoiseSortOrder = permutation;
      } else {
        if (rmsNoise < lowestNoise) {
          lowestNoiseSortOrder = permutation;
          lowestNoise = rmsNoise;
        }
      }
    });

    var result = lowestNoiseSortOrder.map((item) => {
      return stages[item - 1];
    });

    return result;
  }
}
